/*
 * SpeechEngineProbe — detector for phantom Web Speech engines.
 *
 * Problem class:
 *   On some platforms (Huawei without Google Mobile Services, certain
 *   YaBrowser Android builds, Edge with corrupt SAPI install) calling
 *   synth.speak(utterance) returns silently — no audio, no `start`/`end`
 *   event, and critically no `error` event. The existing autoFallback in
 *   document-reader hooks utterance.onerror, which never fires, so the
 *   tool sits at sentence 0 forever. Confirmed 2026-05-15 via bug
 *   c54ca509: doc loaded 501 sentences, stage:playing→paused dwell 64s
 *   at:0/501, zero `synth_error` events. Cross-cluster check showed
 *   0 doc_read_complete events over 30 days against 38 doc_play events.
 *
 * Approach (same shape as the DeviceMotion ground-truth probe used in
 * /vibration-test — measure what actually happens, don't trust API return):
 *
 *   - diagnose(lang) runs a short test utterance once per session-per-lang
 *     and times start/end. Cached in sessionStorage so repeat tools don't
 *     re-probe. Returns a recommendation: 'web_speech' | 'piper' | 'none'.
 *
 *   - makeWatchdog(opts) is a runtime guard around every utterance: if
 *     `start` doesn't fire within `onStartTimeout` ms or `end` doesn't
 *     fire within the chained `onEndTimeoutFromStart` ms, opts.onPhantom
 *     fires with the reason. The caller decides — fallback voice,
 *     promote Piper, surface banner — the module stays policy-free.
 *
 * Reusable across the 25 TTS-using tools in the repo (search:
 * grep -l "SpeechSynthesisUtterance" resources/views/tools).
 *
 * Telemetry helper: SpeechEngineProbe.report(stage, detail) fires
 * feature_use:phantom_speech via __ae when present. Caller can also
 * skip it and emit their own.
 */
(function (global) {
    'use strict';

    var STORAGE_KEY = '__speechEngineProbe.v1';

    function hasSpeechSynthesis() {
        return typeof global.speechSynthesis !== 'undefined'
            && typeof global.SpeechSynthesisUtterance !== 'undefined';
    }

    function getVoicesForLang(langPrefix) {
        if (!hasSpeechSynthesis()) return [];
        var all = global.speechSynthesis.getVoices() || [];
        if (!langPrefix) return all;
        var p = String(langPrefix).toLowerCase().split('-')[0];
        return all.filter(function (v) {
            var vl = (v.lang || '').replace('_', '-').toLowerCase().split('-')[0];
            return vl === p;
        });
    }

    function loadCache() {
        try {
            var raw = global.sessionStorage && global.sessionStorage.getItem(STORAGE_KEY);
            return raw ? JSON.parse(raw) : {};
        } catch (_) { return {}; }
    }
    function saveCache(c) {
        try {
            if (global.sessionStorage) global.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(c));
        } catch (_) {}
    }

    function ae(type, payload) {
        if (typeof global.__ae === 'function') {
            try { global.__ae(type, payload); } catch (_) {}
        }
    }

    /*
     * Pre-flight diagnose. Speaks a 1-char probe utterance at zero volume,
     * times start/end. Result cached per-lang in sessionStorage so a tool
     * that diagnoses twice (or two TTS tools opened back-to-back) reuses
     * the verdict. Falls through with recommend:'web_speech' on any error
     * path the probe can't explain — the runtime watchdog is the final
     * safety net.
     *
     * Returns Promise<{
     *   ok: boolean,                    true = engine is audibly working
     *   reason?: 'no_voices' | 'no_voice_for_lang' | 'no_start' | 'no_end' | 'thrown',
     *   voicesAvailable: number,        getVoices().length
     *   voicesForLang: number,          count matching langPrefix
     *   recommend: 'web_speech' | 'piper' | 'none',
     *   cached: boolean,
     * }>
     *
     * The probe never throws — failure modes resolve with ok:false.
     */
    function diagnose(langPrefix, opts) {
        opts = opts || {};
        var lang = (langPrefix || '').toLowerCase().split('-')[0] || '';
        var cacheKey = 'diag.' + lang;

        if (!opts.force) {
            var cached = loadCache()[cacheKey];
            if (cached && (Date.now() - cached.ts < 30 * 60 * 1000)) {
                return Promise.resolve(Object.assign({ cached: true }, cached.result));
            }
        }

        return new Promise(function (resolve) {
            if (!hasSpeechSynthesis()) {
                return resolve(persist(cacheKey, {
                    ok: false, reason: 'no_voices',
                    voicesAvailable: 0, voicesForLang: 0,
                    recommend: hasPiper(lang) ? 'piper' : 'none',
                }));
            }

            var allVoices = global.speechSynthesis.getVoices() || [];
            var matching = lang ? getVoicesForLang(lang) : allVoices;

            if (allVoices.length === 0) {
                return resolve(persist(cacheKey, {
                    ok: false, reason: 'no_voices',
                    voicesAvailable: 0, voicesForLang: 0,
                    recommend: hasPiper(lang) ? 'piper' : 'none',
                }));
            }

            if (lang && matching.length === 0) {
                return resolve(persist(cacheKey, {
                    ok: false, reason: 'no_voice_for_lang',
                    voicesAvailable: allVoices.length, voicesForLang: 0,
                    recommend: hasPiper(lang) ? 'piper' : 'web_speech',
                }));
            }

            var u, settled = false, startTimer, endTimer;
            try {
                u = new global.SpeechSynthesisUtterance('. ');
                u.volume = 0;
                u.rate = 2;
                if (matching.length) u.voice = matching[0];
            } catch (e) {
                return resolve(persist(cacheKey, {
                    ok: false, reason: 'thrown',
                    voicesAvailable: allVoices.length, voicesForLang: matching.length,
                    recommend: hasPiper(lang) ? 'piper' : 'none',
                }));
            }

            function settle(result) {
                if (settled) return;
                settled = true;
                clearTimeout(startTimer); clearTimeout(endTimer);
                try { global.speechSynthesis.cancel(); } catch (_) {}
                resolve(persist(cacheKey, result));
            }

            u.onstart = function () {
                clearTimeout(startTimer);
                endTimer = setTimeout(function () {
                    settle({
                        ok: false, reason: 'no_end',
                        voicesAvailable: allVoices.length, voicesForLang: matching.length,
                        recommend: hasPiper(lang) ? 'piper' : 'web_speech',
                    });
                }, opts.onEndTimeoutFromStart || 4000);
            };
            u.onend = function () {
                settle({
                    ok: true,
                    voicesAvailable: allVoices.length, voicesForLang: matching.length,
                    recommend: 'web_speech',
                });
            };
            u.onerror = function (e) {
                settle({
                    ok: false, reason: 'synth_error:' + (e && e.error || 'unknown'),
                    voicesAvailable: allVoices.length, voicesForLang: matching.length,
                    recommend: hasPiper(lang) ? 'piper' : 'web_speech',
                });
            };

            startTimer = setTimeout(function () {
                settle({
                    ok: false, reason: 'no_start',
                    voicesAvailable: allVoices.length, voicesForLang: matching.length,
                    recommend: hasPiper(lang) ? 'piper' : 'web_speech',
                });
            }, opts.onStartTimeout || 1500);

            try { global.speechSynthesis.speak(u); }
            catch (e) {
                settle({
                    ok: false, reason: 'thrown',
                    voicesAvailable: allVoices.length, voicesForLang: matching.length,
                    recommend: hasPiper(lang) ? 'piper' : 'none',
                });
            }
        });
    }

    function persist(cacheKey, result) {
        var c = loadCache();
        c[cacheKey] = { ts: Date.now(), result: result };
        saveCache(c);
        return Object.assign({ cached: false }, result);
    }

    function hasPiper(langPrefix) {
        if (!global.PiperEngine
            || typeof global.PiperEngine.isSupported !== 'function'
            || !global.PiperEngine.isSupported()) return false;
        if (!langPrefix) return true;
        try {
            return (global.PiperEngine.availableVoices() || []).some(function (v) {
                return v.lang === langPrefix;
            });
        } catch (_) { return false; }
    }

    /*
     * Runtime watchdog. Wraps a SpeechSynthesisUtterance to detect
     * phantom behaviour (no start, or start-but-no-end). Caller decides
     * what to do on phantom — typical pattern in document-reader is:
     *   1. autoFallbackVoice() — try the next same-lang voice
     *   2. exhausted → promote Piper / show banner
     *
     * Lifecycle:
     *   var wd = SpeechEngineProbe.makeWatchdog({...});
     *   wd.arm();                  // call before/after synth.speak
     *   utterance.addEventListener('start', wd.markStart);
     *   utterance.addEventListener('end', wd.disarm);
     *   utterance.addEventListener('error', wd.disarm);
     *
     * `arm()` starts the no-start timer. `markStart()` cancels it and
     * starts the no-end timer (length derived from expectedDurationMs).
     * `disarm()` clears both. Phantom resolution is fire-once.
     */
    function makeWatchdog(opts) {
        opts = opts || {};
        var onStartTimeout = opts.onStartTimeout || 1500;
        var endMultiplier = opts.endMultiplier || 2.5;
        var endBuffer = typeof opts.endBuffer === 'number' ? opts.endBuffer : 5000;
        var expectedDur = opts.expectedDurationMs || 1000;
        var onPhantom = typeof opts.onPhantom === 'function' ? opts.onPhantom : function () {};

        var startTimer = null, endTimer = null, fired = false;

        function clearStart() { if (startTimer) { clearTimeout(startTimer); startTimer = null; } }
        function clearEnd() { if (endTimer) { clearTimeout(endTimer); endTimer = null; } }

        return {
            arm: function () {
                if (fired) return;
                clearStart();
                startTimer = setTimeout(function () {
                    if (fired) return;
                    fired = true;
                    onPhantom('no_start');
                }, onStartTimeout);
            },
            markStart: function () {
                if (fired) return;
                clearStart();
                clearEnd();
                endTimer = setTimeout(function () {
                    if (fired) return;
                    fired = true;
                    onPhantom('no_end');
                }, Math.max(2000, expectedDur * endMultiplier + endBuffer));
            },
            disarm: function () {
                clearStart(); clearEnd();
                fired = true;
            },
            isFired: function () { return fired; },
        };
    }

    /*
     * How long to wait for `start` before calling an utterance phantom.
     *
     * A WARM engine (it has just spoken) starts fast: 1800 ms for a local voice,
     * 4000 ms for a network one (a fetch before the first sample; audit
     * 2026-07-10). A COLD engine — the first utterance after Play, resume, a voice
     * change or a retry — is another animal. Android 16 / Chrome 133 / Google TTS,
     * measured 2026-10-09: warm 115-163 ms, after 150 s idle 909 ms, first
     * utterance of a fresh browser 4.6-14 s; in the field 31-37 % of Android
     * sessions that pressed Play on /document-reader were declared phantom at
     * exactly 1.80 s, and the same voice read on when the person pressed Play
     * again. Cold budgets are the ones the speech front door already measured
     * (window.__speechDelivery in timbrica-core.js).
     */
    function startBudget(voice, cold) {
        var remote = !!(voice && voice.localService === false);
        var D = global.__speechDelivery || {};
        if (cold) return remote ? (D.COLD_NET_MS || 6000) : (D.COLD_LOCAL_MS || 4000);
        return remote ? 4000 : 1800;
    }

    /*
     * Heuristic for expected utterance duration. Real engines deliver
     * around 10–18 chars/sec depending on voice; we use 6 chars/sec
     * as a conservative floor so the watchdog doesn't false-positive
     * on slow voices or long sentences read at low rate.
     */
    function estimateDurationMs(text, rate) {
        var len = (text || '').length;
        var r = rate || 1;
        return Math.max(800, Math.round(len * 1000 / (6 * r)));
    }

    function report(stage, detail) {
        ae('feature_use', Object.assign({
            slug: (document.querySelector('meta[name="tool-slug"]') || {}).content || null,
            f: 'phantom_speech',
            stage: stage,
        }, detail || {}));
    }

    global.SpeechEngineProbe = {
        diagnose: diagnose,
        makeWatchdog: makeWatchdog,
        startBudget: startBudget,
        estimateDurationMs: estimateDurationMs,
        getVoicesForLang: getVoicesForLang,
        hasPiper: hasPiper,
        report: report,
        _clearCache: function () { saveCache({}); },
    };
})(typeof window !== 'undefined' ? window : this);
